import * as React from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import BootstrapModalConfirm from 'components/bootstrap/BootstrapModalConfirm';
import { Label } from 'components/bootstrap';
import { Icon } from 'components/common';
import Routes from 'routing/Routes';
import { useRemoveEvidence } from 'security-app/hooks/useInvestigationsAPI';
import type {
  InvestigationsDetailsAPIType,
  DashboardEvidenceType,
} from 'security-app/hooks/api/investigationsAPI.types';
import useHistory from 'routing/useHistory';

import DashboardsModal from './DashboardsModal';

import { IconButton, StyledLink } from '../styled.components';

type Props = {
  investigation: InvestigationsDetailsAPIType;
};

const DashboardRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin: 1rem 0;
`;

const DashboardEvidenceList = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  padding-right: 1rem;
`;

const StyledIconButton = styled(IconButton)`
  padding: 0;
`;

const StyledLabel = styled(Label)`
  display: flex;
  flex-direction: row;
  height: 24px;
  gap: 0.4rem;
`;

function Dashboards({ investigation }: Props) {
  const { permissions } = useCurrentUser();
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState<boolean>(false);
  const [deleteDashboardId, setDeleteDashboardId] = React.useState<string>(null);
  const history = useHistory();

  const canManageEvidence = React.useMemo(
    () => !investigation.archived && isPermitted(permissions, 'investigations:edit'),
    [investigation.archived, permissions],
  );

  const { removeEvidence, removingEvidence } = useRemoveEvidence();

  const removeDashboard = async () => {
    await removeEvidence({ invId: investigation.id, payload: { dashboards: [deleteDashboardId] } });
    setShowDeleteDialog(false);
    setDeleteDashboardId(null);
  };

  const onDelete = (dashboardId: string) => (e: React.BaseSyntheticEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
    setDeleteDashboardId(dashboardId);
  };

  const onCancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteDashboardId(null);
  };

  return (
    <>
      <DashboardRow>
        <h2 style={{ marginRight: '1rem' }}>Dashboards</h2>
        {investigation.dashboards.length > 0 && (
          <DashboardEvidenceList>
            {investigation.dashboards.map((dashboard: DashboardEvidenceType) => (
              <StyledLabel key={dashboard.id}>
                <StyledLink onClick={() => history.push(Routes.VIEWS.VIEWID(dashboard.id))}>
                  {dashboard.title}
                </StyledLink>
                {canManageEvidence && (
                  <StyledIconButton $danger data-testid={`delete-${dashboard.id}`} onClick={onDelete(dashboard.id)}>
                    <Icon name="close" />
                  </StyledIconButton>
                )}
              </StyledLabel>
            ))}
          </DashboardEvidenceList>
        )}
        <StyledLabel>
          <StyledIconButton data-testid="add-dashboard" onClick={() => setShowAddModal(true)}>
            <Icon name="add" />
          </StyledIconButton>
        </StyledLabel>
      </DashboardRow>
      <DashboardsModal show={showAddModal} onCancel={() => setShowAddModal(false)} />
      <BootstrapModalConfirm
        showModal={showDeleteDialog}
        title="Delete Dashboard"
        onConfirm={removeDashboard}
        onCancel={onCancelDelete}
        confirmButtonDisabled={removingEvidence}>
        Are you sure you want to delete this Dashboard?
      </BootstrapModalConfirm>
    </>
  );
}

export default Dashboards;
